import { Button, DatePicker, Form, Input, Select, Typography, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
	addEmployeeDocument,
	loadAllEmployeeDocument,
	updateEmployeeDocument,
} from "../../redux/rtk/features/employeeDocument/employeeDocumentSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const { useWatch } = Form;
const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const AddEmployeeDocument = ({
	form,
	isEditMode,
	setIsEditMode,
	currentDocumentId,
	setCurrentDocumentId,
	mode = "admin",
	loggedInEmployeeId,
}) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = useState(false);
	const [fileList, setFileList] = useState([]);
	const [documentNameSelect, setDocumentNameSelect] = useState(undefined);
	const documentNameSelectWatch = useWatch("documentNameSelect", form);
	const showOtherInput = documentNameSelect === "Other" || documentNameSelectWatch === "Other";
	const staffList = useSelector((state) => state.users?.list || []);
	const isUserView = mode === "user";

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	useEffect(() => {
		if (isUserView && loggedInEmployeeId != null) {
			form.setFieldValue("employeeId", loggedInEmployeeId);
		}
	}, [isUserView, loggedInEmployeeId, form]);

	const handleFileChange = (info) => {
		let newFileList = [...info.fileList];
		newFileList = newFileList.slice(-1); // Only keep the last file
		setFileList(newFileList);
	};

	const beforeUpload = (file) => {
		const isPDF = file.type === "application/pdf";
		const isWord =
			file.type ===
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
			file.type === "application/msword";
		const isImage =
			file.type === "image/jpeg" ||
			file.type === "image/png" ||
			file.type === "image/gif" ||
			file.type === "image/webp";
		if (!isPDF && !isWord && !isImage) {
			toast.error("You can only upload PDF, Word documents or images (JPEG, PNG, GIF, WebP)!");
		}
		const isLt10M = file.size / 1024 / 1024 < 10;
		if (!isLt10M) {
			toast.error("File must be smaller than 10MB!");
		}
		return (isPDF || isWord || isImage) && isLt10M;
	};

	const getDocumentName = (values) => {
		const sel = values.documentNameSelect;
		if (sel === "Other" && values.documentNameOther) {
			return values.documentNameOther.trim();
		}
		return sel || "";
	};

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const documentName = getDocumentName(values);
			if (!documentName) {
				toast.error("Please enter or select document name");
				setLoader(false);
				return;
			}

			const formData = new FormData();
			const employeeId = isUserView ? (loggedInEmployeeId ?? values.employeeId) : values.employeeId;
			formData.append("EmployeeDocumentId", isEditMode && currentDocumentId ? String(currentDocumentId) : "0");
			formData.append("EmployeeId", String(employeeId));
			formData.append("DocumentName", documentName);
			if (values.issueDate) {
				formData.append("IssueDate", dayjs(values.issueDate).format("YYYY-MM-DD"));
			}
			if (values.expiryDate) {
				formData.append("ExpiryDate", dayjs(values.expiryDate).format("YYYY-MM-DD"));
			}
			if (values.remarks) {
				formData.append("Remarks", values.remarks);
			}
			if (fileList[0]?.originFileObj) {
				formData.append("documentFile", fileList[0].originFileObj);
			}

			if (isEditMode && currentDocumentId) {
				const resp = await dispatch(
					updateEmployeeDocument({ id: currentDocumentId, formData })
				);
				if (resp.payload?.message === "success") {
					form.resetFields();
					setFileList([]);
					setIsEditMode(false);
					setCurrentDocumentId(null);
					dispatch(loadAllEmployeeDocument(isUserView && loggedInEmployeeId ? { mode: "user", employeeId: loggedInEmployeeId } : { mode: "admin" }));
				}
			} else {
				const resp = await dispatch(addEmployeeDocument(formData));
				if (resp.payload?.message === "success") {
					form.resetFields();
					setFileList([]);
					setDocumentNameSelect(undefined);
					dispatch(loadAllEmployeeDocument(isUserView && loggedInEmployeeId ? { mode: "user", employeeId: loggedInEmployeeId } : { mode: "admin" }));
				}
			}
		} catch (error) {
			toast.error("Failed to save document");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	const resetForm = () => {
		form.resetFields();
		setFileList([]);
		setDocumentNameSelect(undefined);
		setIsEditMode(false);
		setCurrentDocumentId(null);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				{isEditMode ? "Edit Employee Document" : "Generate Employee Document"}
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="employee-document-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				{isUserView ? (
					<Form.Item name="employeeId" hidden>
						<Input style={{ display: "none" }} />
					</Form.Item>
				) : (
					<Form.Item
						label={<span className="required-label">Employee</span>}
						name="employeeId"
						rules={[
							{ required: true, message: "Please select an employee!" },
						]}
					>
						<Select
							placeholder="Select employee"
							showSearch
							optionFilterProp="children"
							filterOption={(input, option) =>
								(option?.children ?? "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
						>
							{staffList?.map((staff) => {
								const employeeId = staff.EmployeeId ?? staff.id;
								if (employeeId == null) return null;
								return (
									<Option key={employeeId} value={employeeId}>
										{staff.EmployeeName ||
											`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
											staff.UserName}
									</Option>
								);
							})}
						</Select>
					</Form.Item>
				)}

				<Form.Item
					label={<span className="required-label">Document Name</span>}
					name="documentNameSelect"
					rules={[
						{ required: true, message: "Please select or enter document name!" },
					]}
				>
					<Select
						placeholder="Select document name"
						allowClear
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
						}
						onChange={(v) => setDocumentNameSelect(v)}
						dropdownStyle={{ zIndex: 9999 }}
					>
						<Option value="Offer Letter">Offer Letter</Option>
						<Option value="Appointment Letter">Appointment Letter</Option>
						<Option value="Experience Certificate">Experience Certificate</Option>
						<Option value="Relieving Letter">Relieving Letter</Option>
						<Option value="Salary Certificate">Salary Certificate</Option>
						<Option value="NOC (No Objection Certificate)">NOC (No Objection Certificate)</Option>
						<Option value="Aadhaar">Aadhaar</Option>
						<Option value="PAN">PAN</Option>
						<Option value="10 certificate">10 certificate</Option>
						<Option value="12th certificate">12th certificate</Option>
						<Option value="Other">Other</Option>
					</Select>
				</Form.Item>

				{showOtherInput && (
					<Form.Item
						label={<span className="required-label">Type Document Name</span>}
						name="documentNameOther"
						rules={[
							{ required: true, message: "Please type document name!" },
						]}
					>
						<Input placeholder="Enter document name" />
					</Form.Item>
				)}

				<Form.Item
					label="Issue Date"
					name="issueDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Form.Item
					label="Expiry Date"
					name="expiryDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
						disabledDate={(current) => {
							const issueDate = form.getFieldValue("issueDate");
							if (!issueDate) return false;
							return current && current < dayjs(issueDate).startOf("day");
						}}
					/>
				</Form.Item>

				<Form.Item
					label="Remarks"
					name="remarks"
				>
					<TextArea
						rows={3}
						placeholder="Enter remarks (optional)"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Upload Document"
					name="documentFile"
				>
					<Dragger
						beforeUpload={beforeUpload}
						onChange={handleFileChange}
						fileList={fileList}
						accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
					>
						<p className="ant-upload-drag-icon">
							<InboxOutlined />
						</p>
						<p className="ant-upload-text">
							Click or drag file to this area to upload
						</p>
						<p className="ant-upload-hint">
							Support for PDF, Word documents and images (JPEG, PNG, GIF, WebP). Max file size: 10MB
						</p>
					</Dragger>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						size="large"
						htmlType="submit"
						block
						loading={loader}
					>
						{isEditMode ? "Update Document" : "Generate Document"}
					</Button>
					{isEditMode && (
						<Button
							size="large"
							onClick={resetForm}
							block
							style={{ marginTop: "10px" }}
						>
							Cancel
						</Button>
					)}
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddEmployeeDocument;

